import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { approvalAPI, ApprovalRequest } from '../services/ApprovalAPI';
import localAuthService from '../services/LocalAuthService';

interface RoleRequest {
  RequestId: string;
  RequestType: string;
  UserDisplayName: string;
  SapUsername: string;
  RoleName: string;
  OldRoleName?: string;
  RequestedBy: string;
  RequestedDate: string;
  Justification: string;
  ManagerName: string;
  ManagerEmail: string;
}

const RejectionPage: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [request, setRequest] = useState<RoleRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Get request data from navigation state or fetch it
    if (location.state?.request) {
      setRequest(location.state.request);
      setLoading(false);
    } else if (requestId) {
      fetchRequestDetails();
    }
  }, [requestId, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRequestDetails = async () => {
    try {
      setLoading(true);
      if (requestId) {
        const requestData = await approvalAPI.getRequestDetails(requestId);
        if (requestData) {
          setRequest(requestData);
        } else {
          setError('Request not found');
        }
      }
    } catch (err) {
      setError('Failed to load request details');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!request || !rejectionReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      // Get authenticated user's email, fallback to manager email if not authenticated
      const userEmail = localAuthService.getUserEmail();
      const approverEmail = userEmail || request.ManagerEmail;

      const approvalRequest: ApprovalRequest = {
        requestId: request.RequestId,
        action: 'reject',
        approverEmail: approverEmail,
        rejectionReason: rejectionReason,
        notes: notes,
        BNAME: request.SapUsername,
        ROLE_NAME: request.RoleName
      };

      const result = await approvalAPI.processApproval(approvalRequest);

      if (result.success) {
        navigate('/approval-success', { 
          state: { 
            action: 'rejected', 
            requestId: request.RequestId,
            userDisplayName: request.UserDisplayName 
          } 
        });
      } else {
        setError(result.message || 'Failed to reject request');
      }
    } catch (err) {
      setError('An error occurred while processing the rejection');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = () => {
    navigate(`/request/approve/${requestId}`, { 
      state: { request } 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Request Not Found</h1>
          <p className="text-gray-600 mb-4">The approval request could not be found.</p>
          <button 
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">Reject Role Change Request</h1>
            <p className="text-gray-600 mt-2">Please provide a reason for rejecting this request</p>
          </div>
        </div>

        {/* Request Summary */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Request Summary</h2>
          </div>
          <div className="px-6 py-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <span className="text-sm font-medium text-gray-500">User:</span>
                  <p className="text-sm text-gray-900">{request.UserDisplayName} ({request.SapUsername})</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Request Type:</span>
                  <p className="text-sm text-gray-900">{request.RequestType}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Role:</span>
                  <p className="text-sm text-gray-900">{request.RoleName}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Requested By:</span>
                  <p className="text-sm text-gray-900">{request.RequestedBy}</p>
                </div>
              </div>
              <div className="mt-4">
                <span className="text-sm font-medium text-gray-500">Justification:</span>
                <p className="text-sm text-gray-900 mt-1">{request.Justification}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Rejection Form */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Rejection Details</h2>
          </div>
          <div className="px-6 py-4 space-y-6">
            <div>
              <label htmlFor="rejectionReason" className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Rejection <span className="text-red-500">*</span>
              </label>
              <select
                id="rejectionReason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                required
              >
                <option value="">Select a reason...</option>
                <option value="Insufficient justification">Insufficient justification</option>
                <option value="User does not meet requirements">User does not meet requirements</option>
                <option value="Security concerns">Security concerns</option>
                <option value="Role not appropriate for user">Role not appropriate for user</option>
                <option value="Temporary access not needed">Temporary access not needed</option>
                <option value="Duplicate request">Duplicate request</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes (Optional)
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Provide additional details about the rejection..."
              />
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleReject}
                disabled={processing || !rejectionReason.trim()}
                className="flex-1 bg-red-600 text-white px-6 py-3 rounded-md font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {processing ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Reject Request
                  </>
                )}
              </button>
              
              <button
                onClick={handleCancel}
                disabled={processing}
                className="flex-1 bg-gray-600 text-white px-6 py-3 rounded-md font-medium hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Cancel
              </button>
            </div>
            
            <div className="mt-4 text-center">
              <button
                onClick={() => navigate('/')}
                className="text-gray-500 hover:text-gray-700 text-sm"
              >
                ← Return to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RejectionPage;
